import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "The service",
    body: "GuardGig28 is a UK members-only platform connecting SIA-licensed security guards with venues needing short-notice cover. We provide the platform: matching, licence verification, GPS timekeeping, and dispute review. GuardGig28 does not provide security services and never handles wages — venues pay guards directly at the posted rate. The working relationship on each shift is between the guard and the venue.",
  },
  {
    title: "Eligibility",
    body: "Guards must be 18+, hold a valid SIA licence for the role they undertake (e.g. Door Supervisor or Security Guarding), pass our verification, and keep that licence current. You can check any guard's licence on the SIA public register. Venues must be legitimate businesses with the authority to book security staff.",
  },
  {
    title: "The rules are the contract",
    body: "Accepting a shift is a commitment. One confirmed no-show means a permanent ban (Rule 01). Late cancellations under 4 hours count as strikes — two strikes in 90 days equals a ban. Payment is released within 60 minutes of GPS-verified checkout (Rule 02). All guards are licence-verified (Rule 03). Both sides rate each other and consistently low ratings lose access (Rule 04). Banned accounts may appeal once, within 72 hours, with documented emergency evidence.",
  },
  {
    title: "Payments & fees",
    body: "Membership costs £5.99 per month for guards and venues alike, cancel anytime — membership is what unlocks claiming and posting shifts. Founding Pass holders are lifetime members. Payment for shifts is agreed and settled directly between the venue and the guard; GuardGig28 never holds, processes, or deducts wages. Any member can report a problem on a shift — our team reviews every report and confirmed misconduct means a ban.",
  },
  {
    title: "Conduct & safety",
    body: "Guards must follow venue lawful instructions and UK licensing law, including the Private Security Industry Act 2001. The panic button connects to platform operations — it does not replace the emergency services; call 999 (or 112) first in life-threatening situations. Abuse of the panic system results in account review.",
  },
  {
    title: "Liability",
    body: "GuardGig28 provides the marketplace and verification layer. We are not the employer of guards and not the provider of security services. Nothing in these terms excludes liability that cannot be excluded under UK law; otherwise, to the maximum extent permitted, our liability is limited to the fees paid through the platform in the preceding 90 days.",
  },
  {
    title: "Termination",
    body: "We may suspend or terminate accounts for rule violations, licence lapse, fraud, or safety risks. You may close your account anytime; completed-shift and payment records are retained as required by law.",
  },
  {
    title: "Governing law",
    body: "These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute arising from them.",
  },
  {
    title: "Changes & contact",
    body: "We may update these terms with notice posted on guardgig28.com. Continued use after notice means acceptance. Questions: email support@guardgig28.com — we answer within 72 hours.",
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#07090e] pb-24">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="terms-back" className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Back</span>
          </Link>
          <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          <span className="font-mono-tag text-[9px] text-slate-600">guardgig28.com</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pt-16 sm:px-6">
        <p className="font-mono-tag text-[11px] text-amber-500">Legal</p>
        <h1 className="font-display mt-4 text-4xl font-black uppercase tracking-tight text-slate-50 sm:text-5xl">Terms of Service</h1>
        <p className="mt-3 font-mono-tag text-[10px] text-slate-500">Effective September 2026 · GuardGig28 · United Kingdom</p>

        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400">
          Plain-English version: the four rules you read on the homepage are the real contract.
          Everything below just says it formally.
        </p>

        <div className="mt-12 space-y-6">
          {sections.map((s, i) => (
            <section key={s.title} data-testid={`terms-section-${i}`} className="rounded-xl border border-white/8 bg-[#0f141c] p-7">
              <h2 className="font-display text-lg font-bold uppercase text-slate-50">
                <span className="mr-3 text-amber-500">{String(i + 1).padStart(2, "0")}</span>{s.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 flex items-center gap-2 text-slate-600">
          <ShieldCheck className="h-4 w-4" />
          <p className="font-mono-tag text-[9px]">GuardGig28 · Serious about work. Fair about everything else.</p>
        </div>
      </main>
    </div>
  );
}
